import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { InputText } from "../../../../components/InputText";
import {
  categoriasApostilasService,
  CategoriaApostila,
} from "../../../../services/categoriasApostilasService";
import {
  FolderOpen,
  Plus,
  Edit,
  Trash2,
  ArrowUp,
  ArrowDown,
  X,
  Save,
  GripVertical,
  BookOpen,
} from "lucide-react";

export function CategoriasApostilas() {
  const [categorias, setCategorias] = useState<CategoriaApostila[]>([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoriaApostila | null>(null);
  const [formNome, setFormNome] = useState("");
  const [formDescricao, setFormDescricao] = useState("");
  const [saving, setSaving] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadCategorias();
  }, []);

  async function loadCategorias() {
    try {
      setLoading(true);
      const data = await categoriasApostilasService.list();
      setCategorias(data);
    } catch {
      enqueueSnackbar("Erro ao carregar categorias", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setFormNome("");
    setFormDescricao("");
    setIsModalOpen(true);
  }

  function openEdit(categoria: CategoriaApostila) {
    setEditing(categoria);
    setFormNome(categoria.nome);
    setFormDescricao(categoria.descricao || "");
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditing(null);
  }

  async function handleSave() {
    if (!formNome.trim()) {
      enqueueSnackbar("O nome da categoria é obrigatório", { variant: "warning" });
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        await categoriasApostilasService.update(editing.id, {
          nome: formNome,
          descricao: formDescricao || undefined,
        });
        enqueueSnackbar("Categoria atualizada com sucesso!", { variant: "success" });
      } else {
        await categoriasApostilasService.create({
          nome: formNome,
          descricao: formDescricao || undefined,
        });
        enqueueSnackbar("Categoria criada com sucesso!", { variant: "success" });
      }
      closeModal();
      loadCategorias();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar categoria",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await categoriasApostilasService.delete(id);
      enqueueSnackbar("Categoria excluída com sucesso!", { variant: "success" });
      setDeletingId(null);
      loadCategorias();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir categoria",
        { variant: "error" }
      );
    }
  }

  async function moveUp(index: number) {
    if (index === 0) return;
    const updated = [...categorias];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const ordens = updated.map((c, i) => ({ id: c.id, ordem: i + 1 }));
    try {
      await categoriasApostilasService.reordenar(ordens);
      setCategorias(updated.map((c, i) => ({ ...c, ordem: i + 1 })));
    } catch {
      enqueueSnackbar("Erro ao reordenar categorias", { variant: "error" });
    }
  }

  async function moveDown(index: number) {
    if (index === categorias.length - 1) return;
    const updated = [...categorias];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const ordens = updated.map((c, i) => ({ id: c.id, ordem: i + 1 }));
    try {
      await categoriasApostilasService.reordenar(ordens);
      setCategorias(updated.map((c, i) => ({ ...c, ordem: i + 1 })));
    } catch {
      enqueueSnackbar("Erro ao reordenar categorias", { variant: "error" });
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <FolderOpen className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Categorias de Apostilas</h1>
            <p className="text-sm text-muted-foreground">
              Organize as apostilas em categorias e defina a ordem de exibição
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova Categoria
        </Button>
      </div>

      {/* Lista */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            Categorias cadastradas ({categorias.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : categorias.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
              <FolderOpen className="h-10 w-10 opacity-30" />
              <p>Nenhuma categoria cadastrada.</p>
              <Button variant="outline" size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Criar primeira categoria
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {categorias.map((categoria, index) => (
                <div
                  key={categoria.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:bg-accent/30 transition-colors"
                >
                  {/* Ordem */}
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {categoria.ordem}
                  </span>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">{categoria.nome}</p>
                    {categoria.descricao && (
                      <p className="text-sm text-muted-foreground truncate">{categoria.descricao}</p>
                    )}
                  </div>

                  {/* Contagem de apostilas */}
                  {categoria.apostilas !== undefined && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                      <BookOpen className="h-3 w-3" />
                      {categoria.apostilas.length} apostila{categoria.apostilas.length !== 1 ? "s" : ""}
                    </span>
                  )}

                  {/* Reordenação */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveDown(index)}
                      disabled={index === categorias.length - 1}
                      className="h-8 w-8 p-0"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Editar */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(categoria)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>

                  {/* Excluir */}
                  {deletingId === categoria.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-destructive font-medium">Confirmar?</span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(categoria.id)}
                        className="h-7 px-2 text-xs"
                      >
                        Sim
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingId(null)}
                        className="h-7 px-2 text-xs"
                      >
                        Não
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingId(categoria.id)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-xl border border-border bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="text-lg font-semibold text-foreground">
                {editing ? "Editar Categoria" : "Nova Categoria"}
              </h2>
              <Button variant="ghost" size="sm" onClick={closeModal} className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <InputText
                  id="nome"
                  placeholder="Ex: Regulamentos, Treinamentos..."
                  value={formNome}
                  onChange={(e) => setFormNome(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição (opcional)</Label>
                <InputText
                  id="descricao"
                  placeholder="Breve descrição da categoria"
                  value={formDescricao}
                  onChange={(e) => setFormDescricao(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
